import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import StarRating from "./StarRating";
import { createFeedback } from "../../services/RatingService";
import { getBookingDetailsFromLocalStorage } from "../../utils/booking";

import "../../style/css/style.css";

interface FeedbackItem {
  title: string;
}

interface FeedbackCardProps {
  item: FeedbackItem;
  onSelect: () => void;
  selected: boolean;
}

const FeedbackCard: React.FC<FeedbackCardProps> = ({
  item,
  onSelect,
  selected,
}) => {
  return (
    <div
      className={`feedback-card ${
        selected ? "selected" : ""
      } flex items-center justify-center w-full font-medium m-2`}
      onClick={onSelect}
    >
      <h3
        className={`font-base text-base ${
          selected ? "font-bold" : ""
        } text-center`}
      >
        {item.title}
      </h3>
    </div>
  );
};

type FeedbackTag = { title: string; type: string };

interface FeedbackFormProps {
  tags: FeedbackTag[];
}

const FeedbackForm: React.FC<FeedbackFormProps> = ({ tags }) => {
  const [rating, setRating] = useState(0);
  const [selectedItems, setSelectedItems] = useState<FeedbackTag[]>([]);
  const [feedbackText, setFeedbackText] = useState("");

  const navigate = useNavigate();

  const handleSelect = (tag: FeedbackTag) => {
    const index = selectedItems.findIndex((item) => item.title === tag.title);
    if (index === -1) {
      setSelectedItems([...selectedItems, { ...tag }]);
    } else {
      const updatedItems = [...selectedItems];
      updatedItems.splice(index, 1);
      setSelectedItems(updatedItems);
    }
  };

  const handleRatingSubmit = async () => {
    const bookingId = getBookingDetailsFromLocalStorage();
    if (!bookingId) {
      console.error("No booking ID found in local storage.");
      navigate("/homepage");
      return;
    }
    try {
      const feedbackData = {
        booking_id: bookingId,
        product_feedback_rating: rating,
        service_feedback_rating: rating, // Assuming the same rating for service
        service_feedback_tags: selectedItems
          .filter((item) => item.type === "service")
          .map((item) => item.title),
        product_feedback_tags: selectedItems
          .filter((item) => item.type === "product")
          .map((item) => item.title),
        images: [], // Add image URLs if available
        feedback_text: feedbackText, // Replace with actual feedback text
      };

      const response = await createFeedback(feedbackData);
      if (response?.status.success) {
        toast.success("Feedback submitted successfully", {
          onClose: () => {
            navigate("/homepage");
          },
        });
      } else {
        const errorMessage =
          response?.status.errorMessage || "Failed to submit feedback";
        toast.error(errorMessage, {
          onClose: () => {
            setRating(0);
            setSelectedItems([]);
          },
        });
      }
    } catch (error) {
      toast.error("Failed to submit feedback", {
        onClose: () => {
          setRating(0);
          setSelectedItems([]);
        },
      });
    }
  };
  return (
    <div>
      <div className="flex flex-col items-center">
        <StarRating rating={rating} setRating={setRating} />
        <div className="feedback-form mt-8 p-4">
          <div className="flex justify-center items-center flex-wrap">
            {tags.map((tag, i) => (
              <FeedbackCard
                key={i}
                item={tag}
                onSelect={() => handleSelect(tag)}
                selected={
                  selectedItems.find((item) => item.title === tag.title) !==
                  undefined
                }
              />
            ))}
          </div>
          <div className="flex flex-col mt-4 px-4">
            <textarea
              name="feedback-text"
              id="feedback-text"
              rows={4}
              className="w-full outline-none border border-[#d9dfe5] focus:border-[#0e97e6] focus:border-[1.5px] p-2 rounded-lg text-base"
              placeholder="Feedback/Suggestions"
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
            />
          </div>
        </div>
      </div>
      <div className="flex justify-center mt-4 pb-6">
        <motion.button
          className={`confirm-button hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg w-[357px] text-sm ${
            rating === 0
              ? "bg-customBackgroundGrey cursor-not-allowed"
              : "bg-customBlue"
          }`}
          disabled={rating === 0}
          onClick={handleRatingSubmit}
          whileTap={{ scale: 0.95 }}
        >
          DONE
        </motion.button>
      </div>
    </div>
  );
};

export default FeedbackForm;
